// ── BCHNostr — Embeddable Creator Support widgets ─────────────────
//
// Lets a creator drop a live fundraising widget for their Creator Support
// GOAL onto any external website. The widget is a standalone, chrome-free page
// (see pages/EmbedWidgetPage.tsx) loaded inside an <iframe>, so it can't touch
// the host site's DOM, cookies, or the visitor's BCHNostr keys — the iframe is
// a hard security boundary, which matters because a single XSS in this app
// drains wallets (see the project security model).
//
// It renders the SAME data the Creator Support page uses (the creator's kind
// 30078 `bch-creator-goal` event + their on-chain BCH balance + kind 9735
// `bch-goal-pledge` receipts), so there is no separate fundraising system —
// just a new view of the existing one.
//
// A Creator Goal is per-creator (one active goal, replaceable), so the widget
// is keyed on the CREATOR'S PUBKEY. Donations stay fully non-custodial: the
// widget never handles funds. Its "Donate with BCH" button opens the creator's
// support page in a new tab, where the visitor pays the creator directly.
//
// Basic analytics (views + donation clicks) are recorded as kind 7027 pings
// signed by a THROWAWAY per-browser keypair — no login required, no PII, and
// the creator can tally them by querying their own pubkey.

import { getPublicKey, generateSecretKey, finalizeEvent, nip19, type EventTemplate } from 'nostr-tools';
import { getRelays } from '../hooks/useNostrPublish';

// Canonical public origin used in generated embed code. Kept explicit (not
// window.location) so the snippet a creator copies always points at the real
// deployed site even when they build it from a preview/staging URL.
export const EMBED_ORIGIN = 'https://bchnostr.com';

export type WidgetType = 'card' | 'button' | 'progress';
export type WidgetTheme = 'light' | 'dark';

export interface EmbedOptions {
  type: WidgetType;
  theme: WidgetTheme;
}

export const WIDGET_TYPES: { id: WidgetType; label: string; desc: string }[] = [
  { id: 'card', label: 'Goal Card', desc: 'Image, title, progress bar and a Donate button.' },
  { id: 'progress', label: 'Progress Bar', desc: 'A slim bar with raised / goal and percentage.' },
  { id: 'button', label: 'Donate Button', desc: 'A compact "Donate with BCH" button on its own.' },
];

/** Recommended iframe dimensions per widget type. Height auto-adjusts at
 *  runtime via postMessage, but these are sensible initial values so there's
 *  no layout jump before the first resize message arrives. */
export const WIDGET_SIZE: Record<WidgetType, { width: number; height: number }> = {
  card: { width: 340, height: 360 },
  progress: { width: 340, height: 96 },
  button: { width: 260, height: 64 },
};

// ── Analytics ─────────────────────────────────────────────────────

/** Embed analytics ping (regular event). See NIP.md § Kind 7027. */
export const EMBED_ANALYTICS_KIND = 7027;
export const EMBED_ANALYTICS_TAG = 'bch-embed';

export type EmbedAction = 'view' | 'donate_click';

// A single throwaway keypair per browser, reused across pings so a creator can
// (optionally) collapse repeat views by the same anonymous viewer. It is NOT a
// BCHNostr identity, never touches localStorage login keys, and holds no funds.
const EMBED_KEY_STORAGE = 'bchnostr:embed-anon-sk';

function getEphemeralSecret(): Uint8Array {
  try {
    const hex = localStorage.getItem(EMBED_KEY_STORAGE);
    if (hex && /^[0-9a-f]{64}$/i.test(hex)) {
      return Uint8Array.from(hex.match(/.{2}/g)!.map(b => parseInt(b, 16)));
    }
  } catch { /* storage disabled — fall through to a session-only key */ }
  const sk = generateSecretKey();
  try {
    const hex = Array.from(sk).map(b => b.toString(16).padStart(2, '0')).join('');
    localStorage.setItem(EMBED_KEY_STORAGE, hex);
  } catch { /* ignore */ }
  return sk;
}

/**
 * Fire-and-forget publish of an embed analytics ping to the app relays.
 *
 * Deliberately best-effort: any failure (offline, relay down, storage
 * blocked) is swallowed so tracking never affects what the visitor sees. A
 * `view` is de-duplicated per creator per browser-session so a page that
 * re-renders doesn't inflate the count. `creatorPubkey` is hex.
 */
export function trackEmbedEvent(action: EmbedAction, creatorPubkey: string): void {
  try {
    if (!creatorPubkey) return;

    if (action === 'view') {
      const seenKey = `bchnostr:embed-viewed:${creatorPubkey}`;
      try {
        if (sessionStorage.getItem(seenKey)) return;
        sessionStorage.setItem(seenKey, '1');
      } catch { /* ignore — count it anyway */ }
    }

    const sk = getEphemeralSecret();
    const template: EventTemplate = {
      kind: EMBED_ANALYTICS_KIND,
      content: '',
      created_at: Math.floor(Date.now() / 1000),
      tags: [
        ['p', creatorPubkey],
        ['t', EMBED_ANALYTICS_TAG],
        ['action', action],
      ],
    };
    const signed = finalizeEvent(template, sk);

    // Publish to the app's write relays over raw WebSockets — no signer, no
    // login. Sockets close themselves after a short grace period.
    for (const url of getRelays().slice(0, 4)) {
      try {
        const ws = new WebSocket(url);
        const close = () => { try { ws.close(); } catch { /* ignore */ } };
        ws.onopen = () => {
          try { ws.send(JSON.stringify(['EVENT', signed])); } catch { /* ignore */ }
          setTimeout(close, 1500);
        };
        ws.onerror = close;
        setTimeout(close, 4000);
      } catch { /* ignore this relay */ }
    }
  } catch { /* analytics must never throw into the render path */ }
}

/** The anonymous pubkey used for this browser's embed pings (for reference). */
export function embedAnonPubkey(): string {
  return getPublicKey(getEphemeralSecret());
}

// ── Identifier helpers ────────────────────────────────────────────

/** Encode a hex pubkey to npub for use in a shareable embed URL. Falls back to
 *  the raw hex if encoding fails, so a bad input never breaks the builder. */
export function pubkeyToNpub(pubkeyHex: string): string {
  try { return nip19.npubEncode(pubkeyHex); } catch { return pubkeyHex; }
}

/** Decode an npub (or accept a raw hex pubkey) back to hex. Returns '' on
 *  anything invalid so the widget page can render a clean fallback. */
export function npubToPubkey(value: string): string {
  const v = (value || '').trim();
  if (/^[0-9a-f]{64}$/i.test(v)) return v.toLowerCase();
  try {
    const decoded = nip19.decode(v);
    if (decoded.type === 'npub' && typeof decoded.data === 'string') return decoded.data;
  } catch { /* ignore */ }
  return '';
}

// ── URL + code generation ─────────────────────────────────────────

/** The iframe URL for a creator's goal widget. `npub` identifies the creator. */
export function buildEmbedUrl(npub: string, opts: EmbedOptions, origin = EMBED_ORIGIN): string {
  const params = new URLSearchParams({ type: opts.type, theme: opts.theme });
  return `${origin}/embed/goal/${encodeURIComponent(npub)}?${params.toString()}`;
}

/** The public support page a donor lands on when they click Donate. */
export function buildSupportUrl(pubkeyHex: string, origin = EMBED_ORIGIN): string {
  return `${origin}/support?creator=${encodeURIComponent(pubkeyHex)}`;
}

/**
 * The copy/paste HTML snippet. Intentionally plain, dependency-free HTML that
 * works on any site (WordPress, Wix, plain HTML, etc.): a responsive iframe
 * plus a tiny inline script that listens for the widget's resize messages.
 *
 * The iframe is capped at `max-width` so it stays responsive on mobile, and
 * the resize script is namespaced + origin-checked so it can't be hijacked by
 * other frames on the host page.
 */
export function buildEmbedCode(npub: string, opts: EmbedOptions, origin = EMBED_ORIGIN): string {
  const url = buildEmbedUrl(npub, opts, origin);
  const { width, height } = WIDGET_SIZE[opts.type];
  const frameId = `bchnostr-embed-${npub.slice(4, 14)}-${opts.type}`;
  return `<iframe
  id="${frameId}"
  src="${url}"
  title="BCHNostr fundraising widget"
  style="width:100%;max-width:${width}px;height:${height}px;border:none;overflow:hidden;"
  loading="lazy"
  scrolling="no"
  referrerpolicy="no-referrer-when-downgrade"
></iframe>
<script>
(function(){
  window.addEventListener("message", function(e){
    if (e.origin !== "${origin}") return;
    var d = e.data || {};
    if (d.type !== "bchnostr-embed-resize" || d.frameId !== "${frameId}") return;
    var f = document.getElementById("${frameId}");
    if (f && d.height) f.style.height = d.height + "px";
  });
})();
</script>`;
}
